<script setup lang="ts">
  import Icon, { IconName } from '@leaflink/stash/Icon.vue';
  import { computed } from 'vue';
  import { I18nT } from 'vue-i18n';

  import { useFeatureEnrollmentProvider } from '@/components/FeatureEnrollment/FeatureEnrollmentLayout.vue';
  import { PosSystemName } from '@/models/inventoryInsights';
  import { i18n } from '@/plugins';
  import { trackWithSegment } from '@/segment';

  import { useInventoryInsightsProvider } from '../InventoryInsights.vue';

  interface POSLink {
    direction: string;
    href: string;
    text: string;
    trackFunction: () => void;
  }

  interface POSInfoConfig {
    systemLink: POSLink;
    nameStep: string;
    locationStep: {
      icon: IconName;
      label: string;
      substeps: string[];
    };
    support: POSLink;
  }

  const { productName, selectedPosSystem } = useInventoryInsightsProvider();
  const { currentStep } = useFeatureEnrollmentProvider();

  const trackPOSEmailClick = () => {
    trackWithSegment({
      event: 'inventoryInsightsEnrollmentInfoClicked',
      properties: {
        pos_system: selectedPosSystem.value,
        product_name: productName,
        element_engaged: 'POS mailto link',
        page_engaged_from: currentStep.value?.text || '',
      },
    });
  };

  const trackPosHelpCenterLinkClick = () => {
    trackWithSegment({
      event: 'inventoryInsightsEnrollmentInfoClicked',
      properties: {
        pos_system: selectedPosSystem.value,
        product_name: productName,
        element_engaged: 'POS Help Center Link',
        page_engaged_from: currentStep.value?.text || '',
      },
    });
  };

  const POS_SYSTEM_INFO: Record<Exclude<PosSystemName, 'other'>, POSInfoConfig> = {
    dutchie: {
      systemLink: {
        direction: 'inventoryInsights.linkDutchie',
        href: "mailto:possupport@dutchie.com?subject=API Key Request - Third Party Integrator&body=Dear Dutchie POS Support,%0A%0AI'm working with LeafLink on an integration and would like to provide API Keys for the following locations:%0A%0A&nbsp;&nbsp;&nbsp;&nbsp;• Location 1 (as it appears in Dutchie POS Backoffice)%0A&nbsp;&nbsp;&nbsp;&nbsp;• Location 2 (as it appears in Dutchie POS Backoffice)%0A&nbsp;&nbsp;&nbsp;&nbsp;• Location 3 (as it appears in Dutchie POS Backoffice)%0A%0ACould you send this to me by end-of-day tomorrow if possible?%0A%0AThanks,",
        text: 'possupport@dutchie.com',
        trackFunction: trackPOSEmailClick,
      },
      nameStep: i18n.t('inventoryInsights.dutchieLinkNameStepDescription'),
      locationStep: {
        icon: 'location',
        label: i18n.t('inventoryInsights.dutchieLocationsStepDescription'),
        substeps: [i18n.t('inventoryInsights.separateApiKey'), i18n.t('inventoryInsights.specifyLocation')],
      },
      support: {
        direction: 'inventoryInsights.moreInformation.dutchieText',
        href: 'https://support.dutchie.com/hc/en-us/articles/27660267271187-Dutchie-POS-API-key-request-process-for-third-party-integrations',
        text: i18n.t('inventoryInsights.moreInformation.dutchieLinkLabel'),
        trackFunction: trackPosHelpCenterLinkClick,
      },
    },
    flowhub: {
      systemLink: {
        direction: 'inventoryInsights.linkFlowhub',
        href: 'https://www.flowhub.com/api-integration-request',
        text: 'flowhub.com/api-integration-request',
        trackFunction: trackPosHelpCenterLinkClick,
      },
      nameStep: i18n.t('inventoryInsights.flowhubLinkNameStepDescription'),
      locationStep: {
        icon: 'document-sent',
        label: i18n.t('inventoryInsights.flowhubLocationsStepDescription'),
        substeps: [
          i18n.t('inventoryInsights.clientId'),
          i18n.t('inventoryInsights.locationId'),
          i18n.t('inventoryInsights.apiKey'),
        ],
      },
      support: {
        direction: 'inventoryInsights.moreInformation.flowhubText',
        href: 'mailto:help@flowhub.com',
        text: i18n.t('inventoryInsights.moreInformation.flowhubLinkLabel'),
        trackFunction: trackPOSEmailClick,
      },
    },
  };

  const selectedSystemInfo = computed(() => POS_SYSTEM_INFO[selectedPosSystem.value]);
</script>

<template>
  <div class="tw-flex tw-flex-col tw-gap-[18px]">
    <I18nT :keypath="selectedSystemInfo.systemLink.direction" scope="global" tag="div">
      <template #link>
        <a
          class="tw-underline"
          :href="selectedSystemInfo.systemLink.href"
          target="_blank"
          @click="selectedSystemInfo.systemLink.trackFunction"
        >
          <span>{{ selectedSystemInfo.systemLink.text }}</span>
        </a>
      </template>
    </I18nT>

    <div class="tw-flex tw-items-center">
      <Icon class="tw-mr-1" name="logo-ll" dense />
      <p class="tw-mb-0 tw-mt-1">{{ selectedSystemInfo.nameStep }}</p>
    </div>

    <div>
      <div class="tw-flex tw-items-center">
        <Icon class="tw-mr-1" :name="selectedSystemInfo.locationStep.icon" dense />
        <p class="tw-mb-0 tw-mt-1">{{ selectedSystemInfo.locationStep.label }}</p>
      </div>

      <ul class="tw-list-disc tw-ml-12">
        <li v-for="substep in selectedSystemInfo.locationStep.substeps" :key="substep">{{ substep }}</li>
      </ul>
    </div>

    <I18nT :keypath="selectedSystemInfo.support.direction" scope="global" tag="div" class="tw-text-ice-700">
      <template #link>
        <a
          class="tw-underline"
          :href="selectedSystemInfo.support.href"
          target="_blank"
          rel="noopener"
          @click="selectedSystemInfo.support.trackFunction"
        >
          {{ selectedSystemInfo.support.text }}
        </a>
        <span>.</span>
      </template>
    </I18nT>
  </div>
</template>
